import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DeclassementBadge from "@/components/DeclassementAlertBadge";
import { fmtDate, batimentColor } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Download, FileSpreadsheet, CalendarRange } from "lucide-react";
import { toast } from "sonner";

export default function Bilan() {
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [semaine, setSemaine] = useState(null);

  const [year, month] = ym.split("-").map(Number);
  const params = { year, month, ...(semaine ? { semaine } : {}) };

  useEffect(() => { setSemaine(null); }, [ym]);

  useEffect(() => {
    api.get("/bilan", { params }).then((r) => setData(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym, semaine]);

  const suffix = `${year}_${String(month).padStart(2, "0")}${semaine ? `_S${String(semaine).padStart(2, "0")}` : ""}`;

  const exportFile = async (path, ext, label) => {
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bilan_autheuil_${suffix}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${label} téléchargé`);
    } catch (e) {
      toast.error("Échec de l'export");
    }
  };
  const exportExcel = () => exportFile("/bilan/export", "xlsx", "Excel");
  const exportCsv = () => exportFile("/bilan/export-csv", "csv", "CSV");

  if (!data) return <div className="text-slate-400">Chargement…</div>;

  const cell = (c) => (c.nb ? `${c.nb} / ${c.net.toFixed(1)}` : "—");
  const chartData = data.rows.map((r) => ({
    date: fmtDate(r.date).slice(0, 5),
    Conforme: r.P1.P.nb + r.P2.P.nb + r.P3.P.nb,
    Déclassé: r.P1.D.nb + r.P2.D.nb + r.P3.D.nb,
  }));

  return (
    <div className="space-y-5" data-testid="bilan">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-slate-900">
            Bilan mensuel
          </h1>
          <Link to="/bilan/annuel" data-testid="bilan-annuel-link" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-500 hover:text-slate-900">
            <CalendarRange className="w-4 h-4" /> Vue annuelle — 12 mois & tendance
          </Link>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input data-testid="bilan-month-input" type="month" value={ym}
            onChange={(e) => setYm(e.target.value)} className="h-12 w-44" />
          <Button data-testid="bilan-export-csv-btn" variant="outline" onClick={exportCsv}
            className="h-12 font-bold">
            <FileSpreadsheet className="w-5 h-5 mr-1" /> Export CSV
          </Button>
          <Button data-testid="bilan-export-excel-btn" onClick={exportExcel}
            className="h-12 bg-slate-900 hover:bg-slate-800 font-bold">
            <Download className="w-5 h-5 mr-1" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Filtre semaine ISO */}
      <div className="flex flex-wrap items-center gap-2" data-testid="bilan-week-filter">
        <span className="text-xs uppercase tracking-widest text-slate-400 font-bold mr-1">Semaine ISO</span>
        <button
          data-testid="bilan-week-all"
          onClick={() => setSemaine(null)}
          className={`px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide transition-colors ${
            semaine === null ? "bg-slate-900 text-white" : "bg-white border-2 border-slate-200 text-slate-600 hover:border-slate-400"}`}
        >
          Mois entier
        </button>
        {data.semaines.map((w) => (
          <button
            key={w}
            data-testid={`bilan-week-${w}`}
            onClick={() => setSemaine(w)}
            className={`px-4 py-2 rounded-lg font-mono font-bold text-sm transition-colors ${
              semaine === w ? "bg-slate-900 text-white" : "bg-white border-2 border-slate-200 text-slate-600 hover:border-slate-400"}`}
          >
            S{w}
          </button>
        ))}
        {data.semaines.length === 0 && <span className="text-sm text-slate-400 font-medium">Aucune semaine avec production</span>}
      </div>

      {/* Taux par bâtiment */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {data.batiments.map((b) => (
          <div key={b} data-testid={`bilan-taux-${b}`} className="rounded-2xl bg-white border-2 border-slate-200 p-5 flex items-center justify-between">
            <div>
              <span className={`px-3 py-1 rounded-md font-heading font-black text-lg ${batimentColor(b)}`}>{b}</span>
              <div className="text-xs text-slate-400 font-bold mt-2 uppercase tracking-wide">Taux déclassement</div>
            </div>
            <DeclassementBadge taux={data.taux[b]} seuil={data.seuil} />
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="rounded-2xl bg-white border-2 border-slate-200 p-5">
        <h2 className="font-heading font-bold text-xl uppercase tracking-tight text-slate-900 mb-4">
          Production hebdomadaire — Conforme vs Déclassé
        </h2>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <XAxis dataKey="date" fontSize={11} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Conforme" stackId="a" fill="#059669" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Déclassé" stackId="a" fill="#e11d48" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-slate-900 text-white">
              <tr className="text-center font-bold uppercase tracking-wide">
                <th className="px-2 py-3">Sem.</th>
                <th className="px-2 py-3 text-left">Date</th>
                <th className="px-2 py-3">P1</th>
                <th className="px-2 py-3 bg-rose-700">Décl. P1</th>
                <th className="px-2 py-3">P2</th>
                <th className="px-2 py-3 bg-rose-700">Décl. P2</th>
                <th className="px-2 py-3">P3</th>
                <th className="px-2 py-3 bg-rose-700">Décl. P3</th>
              </tr>
            </thead>
            <tbody className="font-mono text-center">
              {data.rows.map((r) => (
                <tr key={r.date} className="border-b border-slate-100">
                  <td className="px-2 py-2 text-slate-400 font-bold">S{r.semaine}</td>
                  <td className="px-2 py-2 text-left font-body font-semibold">{fmtDate(r.date)}</td>
                  <td className="px-2 py-2">{cell(r.P1.P)}</td>
                  <td className="px-2 py-2 bg-rose-50 text-rose-800">{cell(r.P1.D)}</td>
                  <td className="px-2 py-2">{cell(r.P2.P)}</td>
                  <td className="px-2 py-2 bg-rose-50 text-rose-800">{cell(r.P2.D)}</td>
                  <td className="px-2 py-2">{cell(r.P3.P)}</td>
                  <td className="px-2 py-2 bg-rose-50 text-rose-800">{cell(r.P3.D)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={8} className="py-12 text-slate-400 font-body">{semaine ? `Aucune production en semaine ${semaine}.` : "Aucune production ce mois-ci."}</td></tr>
              )}
              <tr className="bg-slate-100 font-black border-t-2 border-slate-300">
                <td colSpan={2} className="px-2 py-3 text-left font-body uppercase">{semaine ? `Total S${semaine}` : "Total mois"}</td>
                <td className="px-2 py-3">{cell(data.totals.P1.P)}</td>
                <td className="px-2 py-3 text-rose-800">{cell(data.totals.P1.D)}</td>
                <td className="px-2 py-3">{cell(data.totals.P2.P)}</td>
                <td className="px-2 py-3 text-rose-800">{cell(data.totals.P2.D)}</td>
                <td className="px-2 py-3">{cell(data.totals.P3.P)}</td>
                <td className="px-2 py-3 text-rose-800">{cell(data.totals.P3.D)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[11px] text-slate-400 font-semibold">Format cellule : nb palettes / poids net kg</div>
      </div>
    </div>
  );
}
